// libs
import React, { memo, useState, useEffect, useRef, Fragment, useMemo, useCallback } from 'react';

// contexts
import VirtualizeContext from './VirtualizeContext';

// helpers
import { calulateDimensions, getDisplayRows } from './helpers';

// defs
import { IVirtualizeProps, IRows } from './types';

function Virtualize({ children, parentRef = null }: IVirtualizeProps) {
  const [currentTop, setCurrentTop] = useState(0);
  const [winHeight, setWinHeight] = useState(0);
  const [winWidth, setWinWidth] = useState(0);
  const [width, setWidth] = useState(0);
  const [rows, setRows] = useState<IRows>({});

  const cntRef = useRef<HTMLDivElement>(null);
  let parentRefValue = parentRef?.current;

  const getTopOffset = useCallback(
    () => (parentRefValue ? parentRefValue.scrollTop : window.pageYOffset),
    [parentRefValue]
  );

  useEffect(() => {
    let rafTimer: number;
    let futureTimer: number | undefined;

    const onScrollingStopSetCurrentTop = () => {
      setCurrentTop(getTopOffset());
    };

    const onScroll = () => {
      if (futureTimer !== undefined) {
        window.clearTimeout(futureTimer);
        futureTimer = undefined;
      }

      if (rafTimer !== undefined) {
        window.cancelAnimationFrame(rafTimer);
      }

      rafTimer = window.requestAnimationFrame(() => {
        let newCurrentTop = 0;

        newCurrentTop = getTopOffset();

        // set final scroll top - once scrolling stops
        futureTimer = window.setTimeout(onScrollingStopSetCurrentTop, 500);

        // don't update currentTop until difference is more than half of device height
        if (currentTop !== 0 && Math.abs(newCurrentTop - currentTop) < winHeight / 2) {
          return;
        }

        setCurrentTop(newCurrentTop);
      });
    };

    if (parentRefValue) {
      parentRefValue.addEventListener('scroll', onScroll, { passive: true });
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    // set current scrollTop
    onScroll();

    return () => {
      if (parentRefValue) parentRefValue.removeEventListener('scroll', onScroll);
      else window.removeEventListener('scroll', onScroll);
      if (futureTimer !== undefined) {
        window.clearTimeout(futureTimer);
      }
      if (rafTimer !== undefined) {
        window.cancelAnimationFrame(rafTimer);
      }
    };
  }, [currentTop, getTopOffset, parentRef, parentRefValue, winHeight]);

  useEffect(() => {
    const onResize = () => {
      setWinHeight(window.innerHeight);
      setWinWidth(window.innerWidth);
    };
    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (cntRef.current !== null) {
      const { width: newWidth } = cntRef.current.getBoundingClientRect();
      setWidth(newWidth);
    }
  }, [winWidth]);

  // calculate row dimensions and totalHeight
  const [totalHeight, rowDimensions] = useMemo(() => calulateDimensions(rows), [rows]);

  // get visible rows and styles
  const { visibleRows, paddingTop, paddingBottom } = useMemo(() => {
    let cntTop = 0;
    if (cntRef.current !== null) {
      cntTop = cntRef?.current.getBoundingClientRect().top;
    }

    return getDisplayRows({ rows, rowDimensions, winHeight, currentTop, totalHeight, cntTop });
  }, [rows, rowDimensions, winHeight, currentTop, totalHeight]);

  const setRow = (index: string, rowHeight: number, childRow: React.ReactNode) => {
    setRows((currRows) => ({
      ...currRows,
      [index]: {
        rowHeight,
        childRow,
      },
    }));
  };

  const removeRow = (index: number) => {
    setRows((currRows) => ({
      ...currRows,
      [index]: null,
    }));
  };

  const providerValues = useMemo(
    () => ({
      setRow,
      removeRow,
      width,
    }),
    [width]
  );

  const containerStyles = useMemo(() => {
    return {
      paddingTop: `${paddingTop}px`,
      paddingBottom: `${paddingBottom}px`,
      minHeight: `${totalHeight}px`,
    };
  }, [paddingBottom, paddingTop, totalHeight]);

  return (
    <VirtualizeContext.Provider value={providerValues}>
      <div style={containerStyles} ref={cntRef}>
        {visibleRows.map((row) => (
          <Fragment key={row.index}>{row.childRow}</Fragment>
        ))}
        {children}
      </div>
    </VirtualizeContext.Provider>
  );
}

export default memo(Virtualize);
